import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@noor/ui';
import { toast, useUiStore, type ModalRenderProps } from '@noor/lib';
import { Can } from '@/entities/access';
import { CANCEL_REASONS, cancelReasonLabel, OrderStage } from '@/entities/order';
import { useCancelOrder } from '../model/use-order-actions';

/**
 * Cancel-order modal: pick a reason (required), optionally re-order, and — on
 * stage 8 only — cancel the courier transaction. Ported from cpanel's
 * OrderDialogs cancel flow (reason guard + stage-8 checkbox). The reason is
 * SENT as its Uzbek string, exactly as cpanel's select values were; only the
 * visible label follows the UI language. Like cpanel, the dialog closes on a
 * failed cancel too (the error toast reports it).
 */
export function CancelOrderModal({ payload, close }: ModalRenderProps<'cancel-order'>) {
  const { t } = useTranslation('orders');
  const lang = useUiStore((s) => s.language);
  const mutation = useCancelOrder();

  const [reason, setReason] = useState('');
  const [reorder, setReorder] = useState(false);
  const [cancelTransaction, setCancelTransaction] = useState(false);

  const showTransactionCheckbox = payload.stage === OrderStage.PICK_UP_ARRIVED;

  const onConfirm = () => {
    if (!reason) {
      toast.warning(t('cancelDialog.reasonRequired'));
      return;
    }
    mutation.mutate(
      {
        id: payload.orderId,
        reason,
        reorder,
        cancelTransactions: showTransactionCheckbox && cancelTransaction,
      },
      { onSettled: close },
    );
  };

  return (
    <Can permission="orders.cancel">
      <Modal
        title={t('cancelDialog.title', { number: payload.displayId ?? payload.orderId })}
        description={t('cancelDialog.description')}
        dismissible={!mutation.isPending}
        footer={
          <>
            <Button
              variant="soft"
              size="modal"
              shape="rounded"
              onClick={close}
              disabled={mutation.isPending}
            >
              {t('cancelDialog.back')}
            </Button>
            <Button
              variant="destructive"
              size="modal"
              shape="rounded"
              onClick={onConfirm}
              disabled={mutation.isPending}
            >
              {t('cancelDialog.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason" className="px-[var(--form-label-padding-x)]">
              {t('cancelDialog.reason')}
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="cancel-reason" className="w-full">
                <SelectValue placeholder={t('cancelDialog.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                {CANCEL_REASONS.map((r) => (
                  <SelectItem key={r.uz} value={r.uz}>
                    {cancelReasonLabel(r, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-center gap-3 text-sm font-medium text-form-label-fg">
            <Checkbox checked={reorder} onCheckedChange={(v) => setReorder(v === true)} />
            {t('cancelDialog.reorder')}
          </label>

          {showTransactionCheckbox && (
            <label className="flex items-center gap-3 text-sm font-medium text-form-label-fg">
              <Checkbox
                checked={cancelTransaction}
                onCheckedChange={(v) => setCancelTransaction(v === true)}
              />
              {t('cancelDialog.cancelTransactions')}
            </label>
          )}
        </div>
      </Modal>
    </Can>
  );
}
